import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Download, FileUp, X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, preventAccidentalDismiss } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import { FEEDBACK_IMPORT } from "@/config/dropdownData";
import { GENERIC_ERROR } from "@/lib/errorMessages";
import { FeedbackService } from "@/services/feedbackService";
import { TenantService } from "@/services/tenantService";

const INPUT_ID = "feedback-import-input";

// The server builds it from the columns and types it reads, so the sheet's
// Type dropdown and the import can never disagree.
const downloadTemplate = async () => {
  try {
    const url = URL.createObjectURL(await FeedbackService.importTemplate());
    const link = document.createElement("a");
    link.href = url;
    link.download = "feedback-import-template.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err?.userMessage || GENERIC_ERROR);
  }
};

/**
 * Imports one tenant's feedback from a CSV or Excel file. The tenant comes
 * first: the file names people by email, and only the tenant says whose users
 * they are. The server takes every row or none; a refusal lists each bad row
 * here, so the SuperAdmin can fix the file and upload it again.
 */
const ImportFeedbackModal = ({ onClose, onImported }) => {
  // Live tenants only: the server refuses a closed one.
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [rowErrors, setRowErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    TenantService.fetchTenantList({ pagination: false })
      .then((res) => {
        if (!live) return;
        const options = (res?.data?.tenants ?? []).map((t) => ({ value: t.id, label: t.name || t.slug }));
        setTenants(options.sort((a, b) => a.label.localeCompare(b.label)));
      })
      .catch(() => toast.error("Couldn't load tenants. Close this and try again."));
    return () => {
      live = false;
    };
  }, []);

  const pickTenant = (value) => {
    setTenantId(value);
    setRowErrors([]);
  };

  // A file over the server's limit is refused here, before it uploads.
  const pickFile = (e) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = "";
    const tooLarge = picked && picked.size > FEEDBACK_IMPORT.maxBytes;
    setFile(tooLarge ? null : picked);
    setFileError(tooLarge ? FEEDBACK_IMPORT.messages.fileTooLarge : null);
    setRowErrors([]);
  };

  const removeFile = () => {
    setFile(null);
    setFileError(null);
    setRowErrors([]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await FeedbackService.importFile({ tenantId, file });
      toast.success(`Imported ${res?.data?.imported ?? 0} feedback.`);
      onImported?.();
      onClose?.();
    } catch (err) {
      // A refusal of the file or its rows is said once, in the modal, where
      // the SuperAdmin fixes it. Anything else is a toast.
      const errors = err?.response?.data?.data?.errors;
      if (errors?.length) setRowErrors(errors);
      else if (err?.response?.data?.error_code === "FEEDBACK_IMPORT_FILE_INVALID") setFileError(err.userMessage);
      else toast.error(err?.userMessage || GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const close = () => !busy && onClose?.();

  return (
    <Dialog open onOpenChange={preventAccidentalDismiss(close)}>
      <DialogContent className="w-[340px] md:w-[560px] flex flex-col">
        <DialogHeader className="relative pl-5 pr-14 py-3 border-b border-border-primary shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">Import feedback</DialogTitle>
          <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
            Feedback a tenant&apos;s users gave outside the app. Nobody is emailed.
          </DialogDescription>
          <DialogClose aria-label="Close" disabled={busy} className="absolute right-4 top-3 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5 max-h-[65vh] overflow-y-auto">
          <div>
            <p className="mb-2 text-sm text-text-secondary">
              Tenant<span className="text-status-error font-bold text-[16px] ml-1">*</span>
            </p>
            <Select aria-label="Tenant" value={tenantId} options={tenants} onChange={pickTenant} placeholder="Pick a tenant" searchable disabled={busy} />
          </div>

          <div>
            <p className="mb-2 text-sm text-text-secondary">
              File<span className="text-status-error font-bold text-[16px] ml-1">*</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id={INPUT_ID}
                type="file"
                accept={FEEDBACK_IMPORT.accept}
                className="hidden"
                onChange={pickFile}
                data-testid={INPUT_ID}
                disabled={!tenantId || busy}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!tenantId || busy}
                onClick={() => document.getElementById(INPUT_ID)?.click()}
                className="rounded-lg"
              >
                <FileUp />
                {file ? "Replace file" : "Choose file"}
              </Button>
              {file && (
                <span className="flex min-w-0 items-center gap-1 rounded-lg border border-border-primary py-0.5 pl-2 pr-0.5">
                  <span className="truncate text-[13px] text-text-primary">{file.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Remove ${file.name}`}
                    disabled={busy}
                    onClick={removeFile}
                    className="shrink-0 rounded-full"
                  >
                    <X />
                  </Button>
                </span>
              )}
            </div>
            {fileError && (
              <p role="alert" className="mt-2 text-xs text-status-error">
                {fileError}
              </p>
            )}
            <p className="mt-2 text-xs text-text-secondary">
              A .csv or .xlsx file laid out like the template, one feedback per row. {FEEDBACK_IMPORT.messages.limits} Columns marked * in the template are required;
              Status and Response are optional. Tenant must be the tenant picked above, each email an active user of it, and dates YYYY-MM-DD or DD/MM/YYYY. Status is
              optional and blank means Open; an ended status needs a Response, as it does in the queue, and any other status leaves Response blank. Nobody is emailed.
            </p>
            <Button type="button" variant="link" size="xs" onClick={downloadTemplate} className="h-auto p-0 mt-1">
              <Download />
              Download template
            </Button>
          </div>

          {rowErrors.length > 0 && (
            <div role="alert" className="rounded-lg border border-status-error/40 p-3">
              <p className="mb-2 text-sm text-status-error">Nothing was imported. Fix these rows and upload the file again.</p>
              <ul className="space-y-1 text-[13px]">
                {rowErrors.map((e) => (
                  <li key={e.row} className="flex gap-2">
                    <span className="shrink-0 font-medium">{`Row ${e.row}`}</span>
                    <span>{e.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
          <Button variant="tertiary" type="button" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="secondary" onClick={submit} disabled={!tenantId || !file || busy}>
            {busy ? "Importing…" : "Import"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFeedbackModal;
